import React from 'react';
import {
  Box,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  IconButton,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { Funnel } from 'phosphor-react';

export interface StatCardProps {
  title: string;
  value: string | number;
  percent?: number;
  icon: React.ElementType;
  colorScheme: string;
  isIncrease?: boolean;
  onClick?: () => void;
  onFilter?: () => void;
  isFiltering?: boolean;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  percent,
  icon: IconComponent,
  colorScheme,
  isIncrease = true,
  onClick,
  onFilter,
  isFiltering = false,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue(`${colorScheme}.100`, `${colorScheme}.700`);
  const filteringBorderColor = useColorModeValue(`${colorScheme}.400`, `${colorScheme}.500`);

  return (
    <Box
      bg={isFiltering ? `${colorScheme}.50` : bgColor}
      p={{ base: 2, sm: 2, md: 3 }}
      borderRadius={{ base: 'lg', md: 'xl' }}
      boxShadow={isFiltering ? 'xl' : 'lg'}
      border={isFiltering ? '2px solid' : '1px solid'}
      borderColor={isFiltering ? filteringBorderColor : borderColor}
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-4px)', boxShadow: 'xl' }}
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      position="relative"
      minW={0}
      overflow="hidden"
    >
      {onFilter && (
        <Tooltip
          label={isFiltering ? 'Remover filtro' : 'Filtrar listagem'}
          placement="top"
          hasArrow
        >
          <IconButton
            aria-label={isFiltering ? 'Remover filtro' : 'Filtrar'}
            icon={<Funnel size={14} weight="fill" />}
            size="xs"
            variant={isFiltering ? 'solid' : 'ghost'}
            colorScheme={colorScheme}
            position="absolute"
            bottom={1}
            right={1}
            minW="auto"
            h="auto"
            p={1}
            onClick={e => {
              e.stopPropagation();
              onFilter();
            }}
          />
        </Tooltip>
      )}
      <Flex justify="space-between" align="flex-start" gap={1}>
        <Stat minW={0} flex={1}>
          <StatLabel
            fontSize={{ base: '2xs', sm: 'xs', md: 'sm' }}
            color="gray.500"
            fontWeight="medium"
            noOfLines={2}
            lineHeight="short"
          >
            {title}
          </StatLabel>
          <StatNumber
            fontSize={{ base: 'sm', sm: 'md', md: 'xl' }}
            fontWeight="bold"
            color={`${colorScheme}.600`}
          >
            {value}
          </StatNumber>
          {percent !== undefined && (
            <StatHelpText mb={0} fontSize={{ base: '2xs', sm: 'xs', md: 'sm' }}>
              <StatArrow type={isIncrease ? 'increase' : 'decrease'} />
              {percent.toFixed(1)}%
            </StatHelpText>
          )}
        </Stat>
        <Box
          p={{ base: 1, sm: 1.5, md: 2 }}
          bg={`${colorScheme}.50`}
          borderRadius="md"
          color={`${colorScheme}.500`}
          flexShrink={0}
        >
          <Icon as={IconComponent} boxSize={{ base: 3, sm: 4, md: 5 }} />
        </Box>
      </Flex>
    </Box>
  );
};
